import { FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react"
import axios from "axios"
import EModuleIds from "enums/ModuleEnum"
import { enqueueSnackbar } from "notistack"
import { api } from "../../api"
import { setAlertFunc } from "../../components/AlertToaster/AlertSlice"
import {
    AccountObj,
    HistoryResponse,
    IDocHubDocument,
    IFile,
    IResponse,
    tagsDetail,
    UserInfo,
} from "../Common/commonTypes"
import { UploadDocumentsResponse } from "../Common/fileUploadService"
import { TZohoMapping } from "./accountFields"

export type ContactObj = {
    id: number
    account_id: number
    contact_type: string
    email: string
    name: string
    phone: number | string
    is_primary: 0 | 1
    contact_type_id: number
    is_active: 0 | 1
}
type Location = {
    city: string
    city_id: number
    state: string
    state_id: number
}
export type Zones = Record<string, Location[]>

export type BillingAddressObj = {
    id: number
    account_id: number
    address: string
    city: string
    city_id: number
    gst_certificate_url: IFile
    gstin: string
    is_active: 0 | 1
    is_gst_certificate: boolean
    is_primary: 0 | 1
    pincode: number
    state: string
    state_id: number
    gst_certificate_path: string
    country_id: number
}

export type AccountContactDetails = {
    account_id: number
    contact_type: string
    contact_type_id: number
    country_code: string
    created_at: string
    email: string
    id: number
    is_active: 0 | 1
    is_primary: 0 | 1
    mapping_id: number
    name: string
    phone: string
}

export type AccountBillingAddressDetails = {
    account_id: number
    added_by: number
    address: string
    city: string
    city_id: number
    country_id: number
    gstin: string
    gstin_file_url: string
    id: number
    is_active: 0 | 1
    is_primary: 0 | 1
    pincode: string
    state: string
    state_id: number
}

export type AccountDetailsResponse = {
    account_contacts: AccountContactDetails[]
    account_display_id: string
    account_name: string
    account_type: string
    billing_addresses: AccountBillingAddressDetails[]
    id: number
    is_active: 0 | 1
    legal_business_name: string
    parent_account_name: string
}

export type AccountsTableData = {
    account_contacts: ContactObj[]
    account_name: string
    added_by: Partial<UserInfo>
    billing_addresses: BillingAddressObj[]
    city_id: number
    created_at: string
    linked_projects: Record<string, any>
    account_type: string
    crm_account_id: string
    gstin: string
    id: number
    tags?: {
        others: tagsDetail[]
        primary: tagsDetail[]
    }
    departments?: string[]
    services?: { id: number; name: string }[]
    account_display_id: string
    is_active: 1 | 0
    parent_account_id?: number | null
    parent_account_name: string
    pincode: number
    state_id: number
    updated_at: string
    legal_business_name: string
    operational_location: string
    department: string
    ref_id?: string
    reference_id: string
    source: string
    /** PRD 06: the Zoho Books customer id (vendor id for a vendor). Null when unmapped. */
    zoho_contact_id?: string | null
    zones: Zones
    pan?: string
    msme_reg_number?: string
    msme_reg_type?: string
    gst_treatment?: string
    msme_reg?: 1 | 0
}

export type BankDetailsResponseObj = {
    account_holder_name: string
    account_id: number
    account_number: string
    account_type?: string
    bank_name: string
    created_at: string
    id: number
    ifsc_code: string
    is_active: 1 | 0
    pan_number?: string
    updated_at: string
}
const MODULE_URL = "accounts"

export type AccountDocumentUpload = {
    account_id: number
    /** A Doc Hub `file_type` key (ACCOUNT_DOC_TYPES). */
    file_type: string
    file: File
    /** The document this file replaces; the common flow deletes it and uploads anew. */
    replace_doc_id?: number
}

/** The account's Doc Hub list and its History, both changed by every upload, replace or delete. */
const documentTags = (accountId: number) => [
    { type: "Accounts" as const, id: `documents-${accountId}` },
    { type: "Accounts" as const, id: String(accountId) },
]

/** An account agreement (CRIS-4307): its file is a Doc Hub document of type accounts_agreement. */
export type AccountAgreement = {
    id: number
    account_id: number
    agreement_type: { id: number; name: string | null }
    /** Null when the file was deleted from Doc Hub after the agreement was saved. */
    document: {
        doc_id: number
        document_title: string | null
        link?: { name?: string; urls?: { view?: string; download?: string } } | null
    } | null
    /** yyyy-MM-dd */
    start_date: string
    end_date: string
    updated_by?: { id: number | null; name: string | null }
    updated_at?: string | null
}

export type AgreementType = { id: number; agreement_type: string }

export type AccountAgreementSave = {
    account_id: number
    /** The agreement being edited; omitted to add one. */
    agreement_id?: number
    agreement_type_id: number
    start_date: string
    end_date: string
    /** A new file: uploaded to Doc Hub first, and its doc_id becomes the agreement's document_id. */
    file?: File | null
}

/**
 * Upload one file to the account's Doc Hub: upload_documents (or edit_documents to replace) for a
 * presigned URL, PUT the file to S3, then submit_final_uploads - only after the PUT, or the document
 * stays hidden. Returns the new document's doc_id.
 */
type BaseQueryResult = { data?: unknown; error?: FetchBaseQueryError }
type AccountBaseQuery = (args: FetchArgs) => PromiseLike<BaseQueryResult> | BaseQueryResult

const uploadAccountDocument = async (
    baseQuery: AccountBaseQuery,
    { account_id, file_type, file, replace_doc_id }: AccountDocumentUpload
): Promise<{ data: { doc_id: number } } | { error: FetchBaseQueryError }> => {
    const files = [{ file_type, file_name: [file.name] }]
    const presign = await baseQuery({
        url: replace_doc_id ? "edit_documents" : "upload_documents",
        method: replace_doc_id ? "PUT" : "POST",
        useV3: true,
        body: replace_doc_id
            ? { ticket_id: account_id, is_sub_ticket: true, removed_ids: [replace_doc_id], files }
            : { ticket_id: account_id, is_sub_ticket: true, files },
    } as FetchArgs)
    if (presign.error) return { error: presign.error }
    const target = (presign.data as UploadDocumentsResponse)?.urls?.[0]?.urls?.[0]
    if (!target) return { error: { status: 500, data: { message: "No upload URL was returned." } } }
    try {
        await axios.put(target.presigned_url, file, { headers: { "Content-Type": file.type } })
    } catch {
        return { error: { status: "FETCH_ERROR", error: "The file could not be uploaded." } }
    }
    const final = await baseQuery({
        url: "submit_final_uploads",
        method: "PUT",
        useV3: true,
        body: { ticket_id: account_id, file_infos: [{ file_url: target.file_url, doc_id: target.doc_id }] },
    } as FetchArgs)
    if (final.error) return { error: final.error }
    return { data: { doc_id: target.doc_id } }
}

export type AccountShippingAddress = {
    id: string
    name: string
    address: string
    state: string
    city: string
    pincode: string
    source: string
}

const accountsService = api.injectEndpoints({
    endpoints: build => ({
        getAccounts: build.query<
            { data: AccountsTableData[] },
            {
                added_by?: string
                pa_ids?: string
                parent_account_id?: string
                search_key?: string
                status?: number | ""
                page?: number
                tag_ids?: any
                per_page?: number
                account_type?: "all" | "Client" | "Vendor"
                departments?: string
                services?: string
                source?: string
                zone?: string
                state_id?: string
                city_id?: string
                country_id?: number | ""
                zoho_mapping?: TZohoMapping | ""
            }
        >({
            query: params => ({
                url: `${MODULE_URL}/list_accounts`,
                params: {
                    ...params,
                },
            }),
            providesTags: ["Accounts"],
        }),
        getLinkedAccount: build.query<IResponse<AccountObj[]>, void>({
            query: () => ({
                url: `${MODULE_URL}/get_accounts_for_projects`,
            }),
            providesTags: ["Accounts"],
        }),
        getAccountDetails: build.query<AccountDetailsResponse, number>({
            query: accountId => ({
                url: `/get_account_base_details_by_id`,
                params: {
                    account_id: accountId,
                },
                useV3: true,
            }),
            providesTags: ["Accounts", "Projects"],
        }),
        getAccountServices: build.query<any, void>({
            query: () => ({
                url: `${MODULE_URL}/get_services`,
            }),
            transformResponse: (response: IResponse<any[]>) => response.data,
        }),
        getAccountDepartment: build.query<any, void>({
            query: () => ({
                url: `${MODULE_URL}/get_departments`,
            }),
            transformResponse: (response: IResponse<any[]>) => response.data,
        }),
        getFilterParentAccount: build.query<IResponse<AccountObj[]>, void>({
            query: () => ({
                url: `${MODULE_URL}/get_parent_accounts`,
                params: {
                    search_key: "",
                },
            }),
            providesTags: ["Accounts"],
        }),
        getFormParentAccount: build.query<any[], void>({
            query: () => ({
                url: `${MODULE_URL}/get_accounts_compact`,
                params: {
                    search_key: "",
                },
            }),
            providesTags: ["Accounts"],
        }),
        getAccountTicketHistory: build.query<
            { data: HistoryResponse[]; totalPages: number },
            { account_id: string; page: number; per_page: number }
        >({
            query: params => ({
                url: `${MODULE_URL}/account_history`,
                params: params,
            }),
            providesTags: (result, error, arg) => [{ type: "Accounts", id: arg.account_id }],
        }),

        editAccountStatus: build.mutation<any, { account_id: number; status: 1 | 0 }>({
            query: body => ({
                url: `${MODULE_URL}/edit_account_status`,
                method: "PUT",
                body,
            }),
            invalidatesTags: ["Accounts"],
        }),
        getAccountsAddedBy: build.query<{ added_by: number; name: string }[], void>({
            query: () => ({
                url: `${MODULE_URL}/get_added_by`,
            }),
        }),
        getBankDetails: build.query<BankDetailsResponseObj[], { account_id: number }>({
            query: params => ({
                url: `${MODULE_URL}/bank_details`,
                params: params,
            }),
            providesTags: ["Accounts"],
        }),
        addBankDetails: build.mutation<
            any,
            {
                account_id: number
                bank_details: {
                    bank_name: string
                    account_number: string
                    ifsc_code: string
                    account_holder_name: string
                    is_active: 1 | 0
                }[]
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/bank_details`,
                method: "PUT",
                body,
            }),
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: "Bank Details Updated!" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: ["Accounts"],
        }),
        exportAccounts: build.query<
            Blob,
            {
                added_by: string
                pa_ids: string
                search_key: string
                status: number | ""
                account_type: string
                zoho_mapping?: TZohoMapping | ""
            }
        >({
            query: params => ({
                url: `${MODULE_URL}/export`,
                params: params,
                responseHandler: (response: any) => response.blob(),
            }),
        }),
        getAccountMetrics: build.query<
            {
                all_count: number
                client_count: number
                vendor_count: number
                totalPages: number
            },
            {
                added_by?: string
                pa_ids?: string
                account_type?: "all" | "Client" | "Vendor"
                tag_ids?: any
                search_key?: string
                per_page?: number
                status?: number | ""
                departments?: string
                services?: string
                source?: string
                zone?: string
                state_id?: string
                city_id?: string
                country_id?: number | ""
                zoho_mapping?: TZohoMapping | ""
            }
        >({
            query: params => ({
                url: `${MODULE_URL}/account_metrics`,
                params: params,
            }),
            providesTags: ["Accounts"],
        }),
        // Mutations
        addAccount: build.mutation<
            any,
            {
                account_name: string
                legal_business_name: string
                billing_addresses: any
                account_contacts: any
                parent_account_id?: number
                id?: number
                ref_id?: string
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/account_management`,
                method: "POST",
                body,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    await queryFulfilled
                    enqueueSnackbar("Account created successfully", { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.error?.data?.error || "Something went wrong!", { variant: "error" })
                }
            },

            invalidatesTags: ["Accounts"],
        }),
        editAccount: build.mutation<
            any,
            {
                account_name?: string
                legal_business_name?: string
                billing_addresses?: any
                account_contacts?: any
                parent_account_id?: number
                id?: number
                account_id?: number
                update_contact_info?: number
                refetchTicketApi?: boolean
                ref_id?: string
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/account_management`,
                method: "PUT",
                body: body,
            }),
            onQueryStarted: async (_, { queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error?.message || "Something went wrong!", { variant: "error" })
                }
            },
            invalidatesTags: (result, error, arg) => {
                const tags: any[] = ["Accounts"]

                if (arg?.refetchTicketApi) {
                    tags.push({ type: "Onboarding", id: arg?.account_id })
                }

                return tags
            },
        }),
        editBankDetails: build.mutation<
            any,
            {
                account_id: number
                bank_details: {
                    bank_name: string
                    account_number: string
                    ifsc_code: string
                    account_holder_name: string
                    is_active?: number
                    id?: number
                    account_type?: string
                    account_id?: number
                }[]
            }
        >({
            query: body => ({
                url: `${MODULE_URL}/bank_details`,
                method: "PUT",
                body,
            }),

            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: "Bank Details Updated!" }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message || "Something went wrong!" }))
                }
            },
            invalidatesTags: (result, error, arg) => ["Accounts"],
        }),
        editPanCard: build.mutation({
            query: body => ({
                url: `${MODULE_URL}/pan`,
                method: "PUT",
                useV3: true,
                body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: "Onboarding", id: arg?.account_id }],
        }),
        addBillingAddressForAccount: build.mutation({
            query: body => ({
                url: `${MODULE_URL}/billing_address`,
                method: "POST",
                useV3: true,
                body,
            }),
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error.message, { variant: "error" })
                }
            },
            invalidatesTags: ["Projects", "Accounts"],
        }),

        editBillingAddressForAccount: build.mutation({
            query: body => ({
                url: `${MODULE_URL}/billing_address/edit`,
                method: "PUT",
                useV3: true,
                body,
            }),
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error.message || "Something went wrong!", { variant: "error" })
                }
            },
            invalidatesTags: ["Projects", "Accounts"],
        }),

        addContactForAccount: build.mutation({
            query: body => ({
                url: `${MODULE_URL}/contact`,
                method: "POST",
                useV3: true,
                body,
            }),
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error.message, { variant: "error" })
                }
            },
            invalidatesTags: (result, error, arg: any) => [
                "Projects",
                "Accounts",
                { type: "Onboarding", id: arg?.account_id },
            ],
        }),

        editContactForAccount: build.mutation({
            query: body => ({
                url: `${MODULE_URL}/contact/edit`,
                method: "PUT",
                useV3: true,
                body,
            }),
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error.message, { variant: "error" })
                }
            },
            invalidatesTags: (result, error, arg: any) => [
                "Projects",
                "Accounts",
                { type: "Onboarding", id: arg?.account_id },
            ],
        }),

        // No dispatch / pickup endpoint yet: Billing -> Shipping shows its empty state until there is one.
        getAccountShippingAddresses: build.query<AccountShippingAddress[], { account_id: number; is_vendor: boolean }>({
            queryFn: () => ({ data: [] }),
        }),

        // Doc Hub (PRD 06 §5.3) on the common v3 document APIs: ticket_id is the account id, module 14.
        getAccountDocuments: build.query<IDocHubDocument[], number>({
            query: accountId => ({
                url: "get_documents",
                params: { ticket_id: accountId, module_id: EModuleIds.ACCOUNTS },
                useV3: true,
            }),
            transformResponse: (response: IResponse<IDocHubDocument[]>) => response?.data ?? [],
            providesTags: (_result, _error, accountId) => [{ type: "Accounts", id: `documents-${accountId}` }],
        }),
        /**
         * Upload (or replace) one document: upload_documents / edit_documents for a presigned URL,
         * PUT the file to S3, then submit_final_uploads - only after the PUT, or the document stays hidden.
         * The base query adds the signed-in user_id to each body (Uploaded By and History read it).
         */
        saveAccountDocument: build.mutation<{ message: string }, AccountDocumentUpload>({
            queryFn: async (upload, _api, _extra, baseQuery) => {
                const result = await uploadAccountDocument(baseQuery, upload)
                if ("error" in result) return { error: result.error }
                return { data: { message: upload.replace_doc_id ? "Document replaced." : "Document uploaded." } }
            },
            // A failed replace may already have removed the old document, so the list refetches either way.
            invalidatesTags: (_result, _error, { account_id }) => documentTags(account_id),
        }),
        deleteAccountDocument: build.mutation<{ message: string }, { account_id: number; doc_id: number }>({
            queryFn: async ({ doc_id }, _api, _extra, baseQuery) => {
                const result = await baseQuery({
                    url: "delete_documents",
                    method: "DELETE",
                    useV3: true,
                    body: { ids: [doc_id] },
                } as FetchArgs)
                if (result.error) return { error: result.error }
                return { data: { message: (result.data as { message?: string })?.message || "Document deleted." } }
            },
            invalidatesTags: (_result, error, { account_id }) => (error ? [] : documentTags(account_id)),
        }),

        // Account agreements (CRIS-4307, v3): one account has many. The file is a Doc Hub upload of type
        // accounts_agreement; the agreement keeps its document_id, type and validity dates. The base
        // query adds user_id to each body, and the BE writes every change to the account's History.
        getAgreementTypes: build.query<AgreementType[], void>({
            query: () => ({ url: "agreement_types", useV3: true }),
            transformResponse: (response: IResponse<AgreementType[]>) => response?.data ?? [],
        }),
        getAccountAgreements: build.query<AccountAgreement[], number>({
            query: accountId => ({ url: `accounts/${accountId}/agreements`, useV3: true }),
            transformResponse: (response: IResponse<AccountAgreement[]>) => response?.data ?? [],
            providesTags: (_result, _error, accountId) => [{ type: "Accounts", id: `agreements-${accountId}` }],
        }),
        /**
         * Add or edit an agreement. A new file is uploaded to Doc Hub first (a fresh document, so the
         * previous file stays in Doc Hub if the save then fails), and its doc_id is sent as document_id.
         */
        saveAccountAgreement: build.mutation<AccountAgreement, AccountAgreementSave>({
            queryFn: async (
                { account_id, agreement_id, agreement_type_id, start_date, end_date, file },
                _api,
                _extra,
                baseQuery
            ) => {
                let document_id: number | undefined
                if (file) {
                    const upload = await uploadAccountDocument(baseQuery, {
                        account_id,
                        file_type: "accounts_agreement",
                        file,
                    })
                    if ("error" in upload) return { error: upload.error }
                    document_id = upload.data.doc_id
                }
                const result = await baseQuery({
                    url: agreement_id
                        ? `accounts/${account_id}/agreements/${agreement_id}`
                        : `accounts/${account_id}/agreements`,
                    method: agreement_id ? "PUT" : "POST",
                    useV3: true,
                    body: { agreement_type_id, start_date, end_date, ...(document_id ? { document_id } : {}) },
                } as FetchArgs)
                if (result.error) return { error: result.error }
                return { data: (result.data as IResponse<AccountAgreement>).data }
            },
            // A file may have been uploaded even when the save failed, so Doc Hub (the shared one, tagged
            // plain "Accounts") refetches either way.
            invalidatesTags: (_result, _error, { account_id }) => [
                { type: "Accounts", id: `agreements-${account_id}` },
                ...documentTags(account_id),
                "Accounts",
            ],
        }),
        unmapContactFromAccount: build.mutation({
            query: body => ({
                url: `${MODULE_URL}/contact`,
                method: "DELETE",
                useV3: true,
                body,
            }),
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error.message, { variant: "error" })
                }
            },
            invalidatesTags: ["Projects", "Accounts"],
        }),
    }),
})

export const {
    useGetAccountsQuery,
    useLazyGetAccountsQuery,
    useGetLinkedAccountQuery,
    useGetAccountServicesQuery,
    useGetAccountDepartmentQuery,
    useGetFilterParentAccountQuery,
    useGetFormParentAccountQuery,
    useGetAccountsAddedByQuery,
    useLazyGetAccountTicketHistoryQuery,
    useGetAccountTicketHistoryQuery,
    useLazyExportAccountsQuery,
    useEditAccountStatusMutation,
    useAddAccountMutation,
    useEditAccountMutation,
    useGetAccountMetricsQuery,
    useLazyGetAccountMetricsQuery,
    useGetBankDetailsQuery,
    useAddBankDetailsMutation,
    useEditBankDetailsMutation,
    useLazyGetAccountDetailsQuery,
    useGetAccountDetailsQuery,
    useEditPanCardMutation,
    useAddBillingAddressForAccountMutation,
    useEditBillingAddressForAccountMutation,
    useAddContactForAccountMutation,
    useEditContactForAccountMutation,
    useGetAccountShippingAddressesQuery,
    useGetAccountDocumentsQuery,
    useSaveAccountDocumentMutation,
    useDeleteAccountDocumentMutation,
    useGetAgreementTypesQuery,
    useGetAccountAgreementsQuery,
    useSaveAccountAgreementMutation,
    useUnmapContactFromAccountMutation,
} = accountsService

export default accountsService
